import * as React from "react";
import {
  FormattedMessage,
  injectIntl,
  WrappedComponentProps,
} from "react-intl";
import hexToRgba from "@vingle/wtf-sdk/helpers/hexToRgba";
import WithSelection from "common/components/overlayTooltip/components/withSelection";
import { CollectionSelectItem } from "common/components/overlayTooltip/components/withSelection/rowItem";

interface IProps extends WrappedComponentProps {
  isLoading: boolean;
  isSaving: boolean;
  collections: ReadonlyArray<Vingle.Collection.ICollection>;
  initialSelectedCollection: Vingle.Id[];
  onNewItemCreate: (value: string) => void;
  onSave: (selectedCollection: Vingle.Id[]) => void;
  onCancel: () => void;
}

interface IState {
  selectedCollections: Vingle.Id[];
}

class CollectionTooltipContent extends React.PureComponent<IProps, IState> {
  public state: IState = {
    selectedCollections: this.props.initialSelectedCollection,
  };

  private readonly refWithSelection = React.createRef<WithSelection>();

  public componentDidUpdate(prevProps: IProps) {
    const { initialSelectedCollection, collections } = this.props;
    if (
      prevProps.collections !== collections ||
      prevProps.initialSelectedCollection !== initialSelectedCollection
    ) {
      this.setState({
        selectedCollections: initialSelectedCollection,
      });
      if (
        prevProps.collections.length > 0 &&
        prevProps.collections.length !== collections.length
      ) {
        this.scrollToBottom();
      }
    }
  }

  public render() {
    const { isLoading, onNewItemCreate, onCancel, intl, isSaving } = this.props;
    return (
      <WithSelection
        ref={this.refWithSelection}
        isLoading={isLoading}
        headerText={<FormattedMessage id="select_collection" />}
        onClickNewItem={onNewItemCreate}
        onClickSave={this.handleSave}
        onClickCancel={onCancel}
        createInputPlaceholder={intl.formatMessage({
          id: "add_to_new_collection",
        })}
        disableSaveButton={isSaving}
      >
        {this.renderCollections()}
      </WithSelection>
    );
  }

  private readonly renderCollections = () => {
    const { collections } = this.props;
    const selectedCollections = new Set(this.state.selectedCollections);
    return collections.map(collection => {
      return (
        <CollectionSelectItem
          key={`collectionSelectionTooltip_collection_${collection.id}`}
          title={collection.title}
          collectionSrc={collection.image_urls.s}
          checked={selectedCollections.has(collection.id)}
          color={collection.color ? hexToRgba(collection.color) : undefined}
          isPrivate={!collection.public}
          returnValue={collection.id}
          onChange={this.handleCheckBoxChange}
        />
      );
    });
  };

  private readonly handleCheckBoxChange = (
    checkState: boolean,
    returnValue: any,
  ) => {
    const selectedCollections = new Set(this.state.selectedCollections);
    if (checkState) {
      selectedCollections.add(returnValue);
    } else {
      selectedCollections.delete(returnValue);
    }

    this.setState({
      selectedCollections: Array.from(selectedCollections),
    });
  };

  private readonly handleSave = () => {
    this.props.onSave(this.state.selectedCollections);
  };

  private readonly scrollToBottom = () => {
    const listElement = this.refWithSelection.current;
    if (listElement) {
      listElement.scrollToBottom();
    }
  };
}

export default injectIntl(CollectionTooltipContent);
